import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useUserStore } from '../store/userStore';
import { conversationService } from '../services/conversationService';
import { companyService, Company } from '../services/companyService';
import { userService, User } from '../services/userService';
import { useWebSocket } from '../hooks/useWebSocket';
import MediaViewer from '../components/MediaViewer';
import ConfirmDialog from '../components/ConfirmDialog';
import WhatsAppTemplateModal from '../components/WhatsAppTemplateModal';
import CustomSelect from '../components/CustomSelect';
import { playNewMessageSound } from '../hooks/useSound';
import { whatsappTemplateService } from '../services/whatsappTemplateService';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useProgress } from '../components/TopProgressBar';
import Spinner from '../components/Spinner';
import Button from '../components/Button';

interface Conversation {
  id: string;
  pageId: string;
  senderId: string;
  phoneNumber: string;
  participantName?: string;
  lastMessage: string;
  lastMessageTime: string;
  status: 'active' | 'archived' | 'completed';
  unreadCount?: number;
  updatedAt?: number;
  crmIntegrated?: boolean;
  agentEnabled?: boolean;
  assignedTo?: string;           // userId del asesor asignado (legacy)
  assignedAdvisorId?: string;    // nuevo campo del API
  assignedAdvisorName?: string;  // nuevo campo del API
  canSendFreeform?: boolean;
  hoursSinceLastMessage?: number;
}

interface Message {
  id: string;
  sender: string;
  content: string;
  timestamp: string;
  direction: 'inbound' | 'outbound';
  s3Key?: string;
  mediaType?: string;
  mediaFileName?: string;
  status?: 'sending' | 'sent' | 'error'; // solo para mensajes outbound locales
}

const ConversationsPage: React.FC = () => {
  const { user, canAccessCompany, canAccessConversation } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { start: progressStart, done: progressDone, showLoader, hideLoader } = useProgress();

  // Loader inmediato al montar — antes del primer paint
  useLayoutEffect(() => {
    showLoader('Cargando conversaciones...');
    progressStart();
    return () => { hideLoader(); progressDone(); };
  }, []); // eslint-disable-line

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [webhookData, setWebhookData] = useState<any>(null);
  const [contactSource, setContactSource] = useState('4');
  const [webhookModalTab, setWebhookModalTab] = useState<'form' | 'questions'>('form');

  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [lastConversationKey, setLastConversationKey] = useState<string | undefined>(undefined);
  const [showChatInMobile, setShowChatInMobile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [advisors, setAdvisors] = useState<User[]>([]);
  const [currentAssignment, setCurrentAssignment] = useState<any>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [filterUnread, setFilterUnread] = useState(false);
  const [filterAdvisorId, setFilterAdvisorId] = useState<string>('all');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingAgent, setLoadingAgent] = useState(false);   // toggle agente en cabecera
  const [loadingAssign, setLoadingAssign] = useState(false); // asignar / desasignar

  // ── Panel de conversaciones redimensionable ───────────────────────────────
  const CONV_PANEL_MIN     = 220;
  const CONV_PANEL_MAX     = 400;
  const CONV_PANEL_DEFAULT = 320;

  const [convPanelWidth, setConvPanelWidth] = useState(() => {
    const saved = localStorage.getItem('conv_panel_width');
    if (saved) {
      const n = parseInt(saved, 10);
      if (!isNaN(n) && n >= CONV_PANEL_MIN && n <= CONV_PANEL_MAX) return n;
    }
    return CONV_PANEL_DEFAULT;
  });

  const convPanelRef      = useRef<HTMLDivElement>(null);
  const convDragging      = useRef(false);
  const convDragStartX    = useRef(0);
  const convDragStartWidth = useRef(0);

  // Aplicar el ancho guardado al panel via DOM — solo en desktop (md+)
  useEffect(() => {
    if (!convPanelRef.current) return;
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      // En mobile limpiar cualquier width inline para que w-full del CSS funcione
      convPanelRef.current.style.width    = '';
      convPanelRef.current.style.minWidth = '';
      convPanelRef.current.style.maxWidth = '';
    } else if (!showChatInMobile) {
      convPanelRef.current.style.width    = `${convPanelWidth}px`;
      convPanelRef.current.style.minWidth = `${convPanelWidth}px`;
      convPanelRef.current.style.maxWidth = `${convPanelWidth}px`;
    }
  }, [convPanelWidth, showChatInMobile]);

  const onConvMouseMove = useCallback((e: MouseEvent) => {
    if (!convDragging.current) return;
    const delta = e.clientX - convDragStartX.current;
    const next  = Math.min(CONV_PANEL_MAX, Math.max(CONV_PANEL_MIN, convDragStartWidth.current + delta));
    // Manipular DOM directamente para que sea fluido sin re-renders
    if (convPanelRef.current) {
      convPanelRef.current.style.width    = `${next}px`;
      convPanelRef.current.style.minWidth = `${next}px`;
      convPanelRef.current.style.maxWidth = `${next}px`;
    }
  }, []);

  const onConvMouseUp = useCallback(() => {
    if (!convDragging.current) return;
    convDragging.current = false;
    document.body.style.cursor    = '';
    document.body.style.userSelect = '';
    // Leer el ancho final del DOM y guardarlo en state + localStorage
    const finalWidth = convPanelRef.current
      ? parseInt(convPanelRef.current.style.width, 10)
      : convDragStartWidth.current;
    setConvPanelWidth(finalWidth);
    localStorage.setItem('conv_panel_width', String(finalWidth));
    window.removeEventListener('mousemove', onConvMouseMove);
    window.removeEventListener('mouseup', onConvMouseUp);
  }, [onConvMouseMove]);

  const onConvHandleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    convDragging.current       = true;
    convDragStartX.current     = e.clientX;
    convDragStartWidth.current = convPanelWidth;
    document.body.style.cursor    = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onConvMouseMove);
    window.addEventListener('mouseup', onConvMouseUp);
  };
  
  // Ref para controlar si ya se seleccionó una conversación desde URL
  const hasAutoSelectedRef = useRef(false);
  
  // Estados para diálogos
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Opciones para los selectores
  const sectorOptions = [
    { id: 33, valor: 'Agro Industria' },
    { id: 25, valor: 'Agro Industria' },
    { id: 41, valor: 'Agro Industria' },
    { id: 26, valor: 'Albergue' },
    { id: 27, valor: 'Apicultura' },
    { id: 28, valor: 'Asilo de ancianos' },
    { id: 29, valor: 'Barco' },
    { id: 30, valor: 'Catering' },
    { id: 31, valor: 'Centro de Salud' },
    { id: 32, valor: 'Centro Esparcimiento' },
    { id: 62, valor: 'Centro Naval /Marina de Guerra' },
    { id: 34, valor: 'Clínica' },
    { id: 35, valor: 'Club Deportivo' },
    { id: 36, valor: 'Club Social' },
    { id: 39, valor: 'Colegio' },
    { id: 40, valor: 'Condominios' },
    { id: 42, valor: 'Contratista' },
    { id: 43, valor: 'Distribuidor / Intermediario' },
    { id: 44, valor: 'Domicilio' },
    { id: 60, valor: 'Ejercito' },
    { id: 37, valor: 'Empresa de Telecomunicaciones' },
    { id: 38, valor: 'Empresa de Transporte' },
    { id: 61, valor: 'Fuerza Aerea' },
    { id: 46, valor: 'Hospital' },
    { id: 47, valor: 'Hotel' },
    { id: 48, valor: 'Iglesia' },
    { id: 49, valor: 'Industria en General' },
    { id: 50, valor: 'Lavanderia Comercial' },
    { id: 51, valor: 'Lavanderia Industrial' },
    { id: 52, valor: 'Mina' },
    { id: 56, valor: 'Municipalidad' },
    { id: 45, valor: 'Oficina' },
    { id: 53, valor: 'Peluquería' },
    { id: 54, valor: 'Pesquero' },
    { id: 55, valor: 'Petrolero /Exploracion' },
    { id: 57, valor: 'Restaurante' },
    { id: 58, valor: 'Textil / Confeccionista' },
    { id: 59, valor: 'Universidad' },
  ];

  const condicionOptions = [
    { id: 22, valor: 'EXPORTACIÓN' },
    { id: 23, valor: 'EXW' },
    { id: 20, valor: 'LIMA' },
    { id: 21, valor: 'PROVINCIA' },
    { id: 24, valor: 'PROYECTO' },
  ];
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const selectedConversationRef = useRef<Conversation | null>(null);
  const conversationsListRef = useRef<HTMLDivElement>(null);
  const lastConversationKeyRef = useRef<string | undefined>(undefined);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Mantener la referencia actualizada
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Cargar empresas al montar
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const data = await companyService.getCompanies();
        
        // Filtrar empresas según permisos del usuario
        let filteredCompanies = data;
        if (user && user.role !== 'administrador') {
          filteredCompanies = data.filter(company => {
            const hasAccess = canAccessCompany(company.configId);
            return hasAccess;
          });
        }
        
        setCompanies(filteredCompanies);
        if (filteredCompanies.length > 0) {
          setSelectedCompany(filteredCompanies[0]);
        } else {
          // Sin empresas asignadas — ocultar loader inmediatamente
          hideLoader();
          progressDone();
        }
      } catch (error) {
        console.error('Error loading companies:', error);
      }
    };
    
    if (user) {
      loadCompanies();
    }
  }, [user, canAccessCompany]);

  // Cargar advisors cuando cambia la empresa seleccionada
  useEffect(() => {
    if (!selectedCompany || user?.role === 'asesor') return;
    setFilterAdvisorId('all'); // Resetear filtro al cambiar empresa
    userService.listAdvisors(selectedCompany.configId)
      .then(list => setAdvisors(list))
      .catch(() => setAdvisors([]));
  }, [selectedCompany?.configId, user?.role]);

  const loadConversations = useCallback(async (append: boolean = false) => {
    if (!selectedCompany?.phoneNumberId) return;

    try {
      setLoading(true);
      if (!append) {
        progressStart();
        showLoader('Cargando conversaciones...');
      }
      // Usar pageId en formato whatsapp-{phoneNumberId}
      const pageId = `whatsapp-${selectedCompany.phoneNumberId}`;
      const keyToUse = append ? lastConversationKeyRef.current : undefined;
      const result = await conversationService.getConversations(pageId, 500, keyToUse);
      
      // Filtrar conversaciones según permisos del usuario
      // - Administradores: ven todo
      // - Operadores: ven todas las conversaciones de sus empresas asignadas
      // - Asesores: solo ven conversaciones específicas asignadas
      let filteredConversations = result.conversations;
      
      if (user) {
        if (user.role === 'asesor') {
          filteredConversations = result.conversations.filter(conv => {
            const hasAccess = canAccessConversation(conv.pageId, conv.senderId);
            return hasAccess;
          });
        }
        // operador y administrador ven todas las conversaciones de la empresa
      }
      
      if (append) {
        setConversations(prev => [...prev, ...filteredConversations]);
      } else {
        setConversations(filteredConversations);
        // No resetear selectedConversation si hay parámetros de URL (auto-selección pendiente)
        // o si ya hay una conversación seleccionada
        const hasUrlParams = searchParams.get('pageId') && searchParams.get('senderId');
        if (!hasUrlParams && !selectedConversation) {
          setSelectedConversation(null);
          setMessages([]);
        }
      }
      
      setHasMoreConversations(result.hasMore);
      setLastConversationKey(result.lastKey);
      lastConversationKeyRef.current = result.lastKey;
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast.error('Error al cargar las conversaciones');
    } finally {
      setLoading(false);
      progressDone();
      hideLoader();
    }
  }, [selectedCompany, user, canAccessConversation]);

  // Scroll infinito para cargar más conversaciones con debounce
  const handleConversationsScroll = useCallback(() => {
    const element = conversationsListRef.current;
    if (!element || loading || !hasMoreConversations) return;

    // Limpiar timeout anterior
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Debounce de 150ms
    scrollTimeoutRef.current = setTimeout(() => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      // Cargar más cuando esté a 200px del final (más anticipado)
      if (scrollHeight - scrollTop - clientHeight < 200) {
        loadConversations(true);
      }
    }, 150);
  }, [loading, hasMoreConversations, loadConversations]);

  // Cargar conversaciones cuando cambia la empresa seleccionada
  useEffect(() => {
    loadConversations();
    
    // Cleanup del timeout al desmontar
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [loadConversations]);

  // Seleccionar conversación automáticamente desde URL (solo una vez)
  useEffect(() => {
    // Si ya se seleccionó, no hacer nada
    if (hasAutoSelectedRef.current) return;
    
    const pageIdParam = searchParams.get('pageId');
    const senderIdParam = searchParams.get('senderId');
    
    if (pageIdParam && senderIdParam && conversations.length > 0 && !selectedConversation) {
      // Buscar la conversación que coincida
      const targetConversation = conversations.find(
        conv => conv.pageId === pageIdParam && conv.senderId === senderIdParam
      );
      
      if (targetConversation) {
        hasAutoSelectedRef.current = true; // Marcar como seleccionado
        handleSelectConversation(targetConversation);
        
        // Limpiar los parámetros de la URL después de seleccionar
        setTimeout(() => {
          setSearchParams({});
        }, 100);
      }
    }
  }, [conversations, searchParams, selectedConversation]);

  const pageId = selectedCompany ? `whatsapp-${selectedCompany.phoneNumberId}` : '';

  // Manejar mensajes WebSocket
  const handleWebSocketMessage = useCallback((wsMessage: any) => {
    if (wsMessage.type === 'message') {
      // Solo agregar el mensaje a la vista si es para la conversación actual
      if (selectedConversationRef.current && wsMessage.senderId === selectedConversationRef.current.senderId) {
        const newMessage: Message = {
          id: `ws-${Date.now()}`,
          sender: wsMessage.data.direction === 'incoming' ? (wsMessage.data.senderName || 'Usuario') : 'Bot',
          content: wsMessage.data.message,
          timestamp: new Date(wsMessage.data.timestamp).toISOString(),
          direction: wsMessage.data.direction === 'incoming' ? 'inbound' : 'outbound',
          s3Key: wsMessage.data.s3Key,
          mediaType: wsMessage.data.mediaType,
          mediaFileName: wsMessage.data.mediaFileName,
        };
        setMessages(prev => [...prev, newMessage]);
        // Sonido solo para mensajes entrantes del cliente
        if (wsMessage.data.direction === 'incoming') {
          playNewMessageSound();
        }
      }
    } else if (wsMessage.type === 'conversation_update') {
      // Actualizar conversación en la lista
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.senderId === wsMessage.senderId) {
            // Si es un mensaje entrante y NO es la conversación seleccionada, incrementar unreadCount
            const isIncomingMessage = wsMessage.data.direction === 'incoming';
            const isSelectedConversation = selectedConversationRef.current?.senderId === wsMessage.senderId;
            
            // Sonido para mensajes entrantes en conversaciones no abiertas
            if (isIncomingMessage && !isSelectedConversation) {
              playNewMessageSound();
            }

            return {
              ...conv,
              lastMessage: wsMessage.data.lastMessage,
              lastMessageTime: new Date(wsMessage.data.updatedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
              unreadCount: isIncomingMessage && !isSelectedConversation 
                ? (conv.unreadCount || 0) + 1 
                : conv.unreadCount,
              updatedAt: wsMessage.data.updatedAt // Actualizar timestamp para ordenamiento
            };
          }
          return conv;
        });
        
        // Reordenar: mover la conversación actualizada al principio
        const sortedConversations = updated.sort((a, b) => {
          const aTime = a.updatedAt || 0;
          const bTime = b.updatedAt || 0;
          return bTime - aTime;
        });
        
        // Si la conversación no existe en la lista, verificar si el usuario tiene acceso antes de recargar
        const conversationExists = updated.some(conv => conv.senderId === wsMessage.senderId);
        if (!conversationExists) {
          // Si es asesor, verificar si tiene acceso a esta conversación
          if (user?.role === 'asesor') {
            const hasAccess = canAccessConversation(wsMessage.pageId || pageId, wsMessage.senderId);
            if (hasAccess) {
              loadConversations();
            }
          } else {
            // Administradores y operadores siempre recargan
            loadConversations();
          }
        }
        
        return sortedConversations;
      });
    }
  }, [loadConversations, user, canAccessConversation, pageId]);

  // Conectar WebSocket con el senderId de la conversación seleccionada
  useWebSocket(pageId, selectedConversation?.senderId, handleWebSocketMessage);

  const scrollToBottom = (instant = false) => {
    messagesEndRef.current?.scrollIntoView({ behavior: instant ? 'auto' : 'smooth' });
  };

  // Scroll inteligente: espera que las imágenes carguen antes de scrollear al final
  const scrollToBottomAfterImages = () => {
    const container = messagesEndRef.current?.parentElement;
    if (!container) {
      scrollToBottom(true);
      return;
    }
    const images = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
    const pending = images.filter(img => !img.complete);
    if (pending.length === 0) {
      // Sin imágenes pendientes — scroll inmediato
      scrollToBottom(true);
    } else {
      // Esperar que todas las imágenes carguen
      let loaded = 0;
      const onLoad = () => {
        loaded++;
        if (loaded >= pending.length) scrollToBottom(true);
      };
      pending.forEach(img => {
        img.addEventListener('load', onLoad, { once: true });
        img.addEventListener('error', onLoad, { once: true }); // también en error para no quedarse esperando
      });
      // Fallback: si en 3s no cargaron, scrollear igual
      setTimeout(() => scrollToBottom(true), 3000);
    }
  };

  useEffect(() => {
    // Al cambiar mensajes: scroll inmediato sin animación (carga inicial)
    // y luego otro tras imágenes
    scrollToBottom(true);
    scrollToBottomAfterImages();
  }, [messages]);

  const handleSelectConversation = async (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setShowChatInMobile(true);
    setCurrentAssignment(null);
    setMessages([]);         // limpiar mensajes anteriores
    setLoadingMessages(true); // activar skeleton

    try {
      // Cargar mensajes y prefetch de asignación en paralelo
      const messagesPromise = conversationService.getMessages(conversation.pageId, conversation.senderId);

      // Solo hacemos el fetch de asignación si no viene el dato en la conversación
      if (!conversation.assignedAdvisorId && user?.role !== 'asesor') {
        userService.getConversationAssignment(conversation.pageId, conversation.senderId)
          .then(data => setCurrentAssignment(data.assigned ? data.assignment : null))
          .catch(() => {/* silencioso — no es crítico */});
      } else if (conversation.assignedAdvisorId) {
        // Usamos el dato que ya viene, sin fetch extra
        setCurrentAssignment({
          userId:        conversation.assignedAdvisorId,
          advisorName:   conversation.assignedAdvisorName || conversation.assignedAdvisorId,
          advisorEmail:  '',
          assignedByName: '',
          assignedAt:    '',
        });
      }

      const data = await messagesPromise;
      setMessages(data);
      setLoadingMessages(false);

      // Resetear contador de no leídos
      if (conversation.unreadCount && conversation.unreadCount > 0) {
        await conversationService.markAsRead(conversation.pageId, conversation.senderId);
        setConversations(prev => prev.map(conv =>
          conv.senderId === conversation.senderId
            ? { ...conv, unreadCount: 0 }
            : conv
        ));
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setLoadingMessages(false);
    }
  };

  const handleBackToConversations = () => {
    setShowChatInMobile(false);
    setSelectedConversation(null);
  };

  const handleToggleAgent = async () => {
    if (!selectedConversation) return;
    if (user?.role === 'asesor') return;

    try {
      setLoadingAgent(true);
      showLoader(selectedConversation.agentEnabled !== false ? 'Desactivando agente...' : 'Activando agente...');
      const newAgentEnabled = await conversationService.toggleAgent(
        selectedConversation.pageId,
        selectedConversation.senderId
      );

      setSelectedConversation({
        ...selectedConversation,
        agentEnabled: newAgentEnabled,
      });

      setConversations(prevConversations =>
        prevConversations.map(conv =>
          conv.senderId === selectedConversation.senderId && conv.pageId === selectedConversation.pageId
            ? { ...conv, agentEnabled: newAgentEnabled }
            : conv
        )
      );

      toast.success(newAgentEnabled ? 'Agente activado' : 'Agente desactivado');
    } catch (error) {
      console.error('Error toggling agent:', error);
      toast.error('Error al cambiar el estado del agente');
    } finally {
      setLoadingAgent(false);
      hideLoader();
    }
  };

  const handleSendReply = async () => {
    if (!selectedConversation || !replyMessage.trim()) return;

    const messageToSend = replyMessage.trim();
    setReplyMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = '20px';
    }

    // ID temporal para el mensaje optimista
    const tempId = `temp-${Date.now()}`;
    const tempMessage: Message = {
      id: tempId,
      sender: user?.name || 'Agente',
      content: messageToSend,
      timestamp: new Date().toISOString(),
      direction: 'outbound',
      status: 'sending',
    };

    // Mostrar inmediatamente en el chat
    setMessages(prev => [...prev, tempMessage]);

    try {
      setLoading(true);
      await conversationService.sendMessage(selectedConversation.pageId, selectedConversation.senderId, messageToSend);

      // Marcar como enviado
      setMessages(prev => prev.map(m => m.id === tempId ? { ...m, status: 'sent' } : m));

      // Sincronizar mensajes reales en background
      conversationService.getMessages(selectedConversation.pageId, selectedConversation.senderId)
        .then(data => setMessages(data))
        .catch(() => {});

      // Actualizar lista de conversaciones en background
      if (selectedCompany?.phoneNumberId) {
        const pageId = `whatsapp-${selectedCompany.phoneNumberId}`;
        conversationService.getConversations(pageId).then(result => {
          let filteredConversations = result.conversations;
          if (user?.role === 'asesor') {
            filteredConversations = result.conversations.filter(conv =>
              canAccessConversation(conv.pageId, conv.senderId)
            );
          }
          setConversations(filteredConversations);
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Marcar como error
      setMessages(prev => prev.map(m => m.id === tempId ? { ...m, status: 'error' } : m));
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedConversation) return;

    try {
      setLoading(true);
      setIsUploading(true);
      setUploadProgress(0);
      
      // Determinar tipo de archivo
      let fileType = 'document';
      if (file.type.startsWith('image/')) {
        fileType = 'image';
      } else if (file.type.startsWith('video/')) {
        fileType = 'video';
      } else if (file.type.startsWith('audio/')) {
        fileType = 'audio';
      }

      // Usar método diferente según el tamaño del archivo
      const FILE_SIZE_LIMIT = 8 * 1024 * 1024; // 8MB (dejamos margen bajo el límite de 10MB)
      
      if (file.size > FILE_SIZE_LIMIT) {
        // Archivos grandes: subir directamente a S3
        await conversationService.sendLargeFile(
          selectedConversation.pageId,
          selectedConversation.senderId,
          file,
          fileType,
          replyMessage || undefined,
          (progress) => setUploadProgress(progress)
        );
      } else {
        // Archivos pequeños: método tradicional
        await conversationService.sendFileBlob(
          selectedConversation.pageId,
          selectedConversation.senderId,
          file,
          fileType,
          replyMessage || undefined
        );
      }

      setReplyMessage('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Reload messages
      const data = await conversationService.getMessages(selectedConversation.pageId, selectedConversation.senderId);
      setMessages(data);
      // Reload conversations to update last message pero mantener la conversación seleccionada
      if (selectedCompany?.phoneNumberId) {
        const pageId = `whatsapp-${selectedCompany.phoneNumberId}`;
        const result = await conversationService.getConversations(pageId);
        
        // Aplicar el mismo filtro que en loadConversations
        let filteredConversations = result.conversations;
        if (user?.role === 'asesor') {
          filteredConversations = result.conversations.filter(conv => 
            canAccessConversation(conv.pageId, conv.senderId)
          );
        }
        
        setConversations(filteredConversations);
      }
    } catch (error) {
      console.error('Error sending file:', error);
      toast.error('Error al enviar el archivo. Intenta de nuevo.');
    } finally {
      setLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const filteredConversations = conversations.filter((conv) => {
    const matchesSearch =
      conv.phoneNumber.includes(searchTerm) ||
      (conv.participantName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesUnread = !filterUnread || (conv.unreadCount ?? 0) > 0;
    const matchesAdvisor =
      filterAdvisorId === 'all' ? true :
      filterAdvisorId === 'unassigned' ? !conv.assignedAdvisorId :
      conv.assignedAdvisorId === filterAdvisorId;
    return matchesSearch && matchesUnread && matchesAdvisor;
  });

  // Extraer preguntas y respuestas del flujo
  const extractQuestionsAndAnswers = () => {
    const questionsAnswers: Array<{ question: string; answer: string }> = [];
    
    // Buscar patrones de pregunta-respuesta en los mensajes
    for (let i = 0; i < messages.length - 1; i++) {
      const currentMsg = messages[i];
      const nextMsg = messages[i + 1];
      
      // Si el mensaje actual es del bot (outbound) y el siguiente es del usuario (inbound)
      if (currentMsg.direction === 'outbound' && nextMsg.direction === 'inbound') {
        // Verificar que sea una pregunta (termina con ?)
        if (currentMsg.content.includes('?')) {
          questionsAnswers.push({
            question: currentMsg.content,
            answer: nextMsg.content,
          });
        }
      }
    }
    
    return questionsAnswers;
  };

  const handleOpenWebhookModal = () => {
    if (!selectedCompany?.urlWebHook) {
      toast.info('La empresa no tiene configurado un webhook');
      return;
    }

    const questionsAnswers = extractQuestionsAndAnswers();
    
    // Crear descripción solo con las respuestas del cliente
    const clientResponses = questionsAnswers
      .map((qa: any) => qa.answer)
      .join(' | ');
    
    // Agregar fecha actual al inicio de la descripción (formato: DD-MM-YYYY)
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const currentDate = `${day}-${month}-${year}`;
    const descripcionConFecha = `${currentDate} | ${clientResponses}`;
    
    const dataToSend = {
      phoneNumber: selectedConversation?.phoneNumber,
      participantName: selectedConversation?.participantName || 'Sin nombre',
      questionsAnswers: questionsAnswers,
      contactSource: contactSource,
      sector: '50', // Valor por defecto: Lavandería Comercial
      condicion: '20', // Valor por defecto
      descripcion: descripcionConFecha,
      timestamp: new Date().toISOString(),
    };

    setWebhookData(dataToSend);
    setShowWebhookModal(true);
  };

  const handleSendToWebhook = async () => {
    if (!webhookData || !selectedCompany?.urlWebHook || !selectedConversation) return;

    try {
      setLoading(true);
      
      // Transformar los datos al formato esperado por la API
      const transformedData = {
        celular: webhookData.phoneNumber.replace(/\D/g, ''), // Solo números
        clieNombre: webhookData.participantName,
        clieNumero: '', // No disponible en los datos actuales
        clieTipDoc: '', // No disponible en los datos actuales
        condicion: parseInt(webhookData.condicion), // ID de la condición
        clieDireccion: '', // No disponible en los datos actuales
        email: '', // No disponible en los datos actuales
        fuente: parseInt(webhookData.contactSource), // ID de la fuente
        sector: parseInt(webhookData.sector), // ID del sector
        telefono: webhookData.phoneNumber.replace(/\D/g, ''), // Solo números
        contacto: webhookData.participantName,
        puesto: '', // No disponible en los datos actuales
        descripcion: webhookData.descripcion, // Descripción editada por el usuario
        usuario: '', // Se puede obtener del usuario logueado si es necesario
        operacion: 'saveLead',
      };
      
      await conversationService.sendToWebhook(selectedCompany.urlWebHook, transformedData);
      
      // Marcar la conversación como integrada con CRM
      await conversationService.markAsCrmIntegrated(selectedConversation.pageId, selectedConversation.senderId);
      
      // Actualizar la conversación en la lista local
      setConversations(prev => prev.map(conv =>
        conv.senderId === selectedConversation.senderId
          ? { ...conv, crmIntegrated: true }
          : conv
      ));
      
      // Actualizar la conversación seleccionada
      setSelectedConversation(prev => prev ? { ...prev, crmIntegrated: true } : null);
      
      toast.success('Datos enviados al webhook');
      setShowWebhookModal(false);
      setWebhookData(null);
    } catch (error) {
      console.error('Error sending to webhook:', error);
      toast.error('Error al enviar al webhook');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignModal = async () => {
    if (!user || (user.role !== 'administrador' && user.role !== 'operador')) return;
    if (!selectedCompany || !selectedConversation) {
      toast.error('No hay empresa o conversación seleccionada');
      return;
    }

    if (advisors.length === 0) {
      toast.info('No hay asesores asignados a esta empresa. Asígnalos desde el panel de usuarios.');
      return;
    }

    setShowAssignModal(true);
  };

  const handleUnassignConversation = async () => {
    if (!currentAssignment || !selectedConversation) return;
    
    setConfirmDialog({
      isOpen: true,
      title: 'Desasignar conversación',
      message: `¿Desasignar esta conversación de ${currentAssignment.advisorName}?`,
      type: 'warning',
      onConfirm: async () => {
        try {
          setLoadingAssign(true);
          showLoader('Desasignando asesor...');
          await userService.unassignConversation(
            currentAssignment.userId,
            selectedConversation.pageId,
            selectedConversation.senderId
          );
          
          setCurrentAssignment(null);

          const updated = { ...selectedConversation, assignedAdvisorId: undefined, assignedAdvisorName: undefined };
          setSelectedConversation(updated);
          setConversations(prev => prev.map(c =>
            c.senderId === selectedConversation.senderId && c.pageId === selectedConversation.pageId
              ? { ...c, assignedAdvisorId: undefined, assignedAdvisorName: undefined }
              : c
          ));

          toast.success('Conversación desasignada');
        } catch (error) {
          console.error('Error unassigning conversation:', error);
          toast.error('Error al desasignar');
        } finally {
          setLoadingAssign(false);
          hideLoader();
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }
      },
    });
  };

  const handleAssignConversation = async (advisorId: string) => {
    if (!selectedConversation) return;
    
    try {
      setLoadingAssign(true);
      showLoader('Asignando asesor...');
      await userService.assignConversation(
        advisorId,
        selectedConversation.pageId,
        selectedConversation.senderId
      );

      const advisor = advisors.find(a => a.userId === advisorId);
      const advisorName = advisor?.name || advisorId;

      const assignmentData = await userService.getConversationAssignment(
        selectedConversation.pageId,
        selectedConversation.senderId
      );
      setCurrentAssignment(assignmentData.assigned ? assignmentData.assignment : null);

      const updated = { ...selectedConversation, assignedAdvisorId: advisorId, assignedAdvisorName: advisorName };
      setSelectedConversation(updated);
      setConversations(prev => prev.map(c =>
        c.senderId === selectedConversation.senderId && c.pageId === selectedConversation.pageId
          ? { ...c, assignedAdvisorId: advisorId, assignedAdvisorName: advisorName }
          : c
      ));
      
      toast.success('Conversación asignada');
      setShowAssignModal(false);
    } catch (error) {
      console.error('Error assigning conversation:', error);
      toast.error('Error al asignar');
    } finally {
      setLoadingAssign(false);
      hideLoader();
    }
  };

  const handleSendTemplate = async (templateName: string, languageCode: string, components: any[]) => {
    if (!selectedConversation || !selectedCompany) return;

    try {
      setLoading(true);
      
      await whatsappTemplateService.sendTemplate(
        selectedCompany.phoneNumberId,
        selectedConversation.phoneNumber,
        templateName,
        languageCode,
        components,
        user?.name || 'Asesor' // Pasar el nombre del usuario
      );

      toast.success('Plantilla enviada');
      
      // Reload messages sin cerrar la conversación
      const data = await conversationService.getMessages(selectedConversation.pageId, selectedConversation.senderId);
      setMessages(data);
      
      // NO recargar todas las conversaciones para evitar que se cierre el chat
      // El WebSocket ya actualizará la lista automáticamente
    } catch (error) {
      console.error('Error sending template:', error);
      toast.error('Error al enviar la plantilla. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const get24HourWindowStatus = () => {
    if (!selectedConversation?.hoursSinceLastMessage) return { color: 'green', text: 'Ventana abierta', icon: 'open' };
    
    const hours = selectedConversation.hoursSinceLastMessage;
    const remaining = Math.floor(24 - hours);

    if (hours < 20) {
      return { color: 'green', text: `Ventana: ${remaining}h restantes`, icon: 'open' };
    } else if (hours < 24) {
      return { color: 'yellow', text: `Ventana: ${remaining}h restantes`, icon: 'warn' };
    } else {
      return { color: 'red', text: 'Ventana cerrada', icon: 'closed' };
    }
  };

  return (
    <div className="flex h-full bg-brand-bg overflow-hidden relative">
      {/* ── Panel izquierdo: lista de conversaciones ───────────────── */}
      <div
        ref={convPanelRef}
        className={`${showChatInMobile ? 'hidden' : 'flex'} md:flex bg-brand-surface flex-col border-r border-brand-border relative flex-shrink-0 overflow-hidden
          w-full md:w-auto`}
      >
        {/* Header del panel */}
        <div className="px-5 pt-5 pb-4 border-b border-brand-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-lg font-bold text-brand-dark">Sesiones activas</h1>
              <span className={`inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                filteredConversations.length > 0
                  ? 'bg-brand-yellow/20 text-brand-dark'
                  : 'bg-brand-mid/10 text-brand-mid'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${
                  filteredConversations.length > 0 ? 'bg-green-500 animate-pulse' : 'bg-brand-mid/40'
                }`}></span>
                {filteredConversations.length} {filterUnread ? 'NO LEÍDAS' : 'ACTIVAS'}
              </span>
            </div>
          </div>

          {/* Filtros — dos líneas con labels */}
          <div className="space-y-2 mb-3">
            {/* Línea 1: Empresa + botón actualizar */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-brand-mid flex-shrink-0 w-14">Empresa:</span>
              <CustomSelect
                value={selectedCompany?.configId || ''}
                onChange={(val) => {
                  const company = companies.find(c => c.configId === val);
                  if (company) setSelectedCompany(company);
                }}
                placeholder="Selecciona..."
                className="flex-1 min-w-0"
                options={companies.map(c => ({ value: c.configId, label: c.nombreEmpresa }))}
              />
              <button
                onClick={() => loadConversations(false)}
                disabled={loading}
                title="Actualizar conversaciones"
                className="p-2 hover:bg-brand-light rounded-lg transition disabled:opacity-50 text-brand-mid hover:text-brand-dark flex-shrink-0"
              >
                <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>

            {/* Línea 2: Asesor — solo si hay asesores */}
            {advisors.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-brand-mid flex-shrink-0 w-14">Asesor:</span>
                <CustomSelect
                  value={filterAdvisorId ?? 'all'}
                  onChange={(val) => setFilterAdvisorId(val)}
                  className="flex-1 min-w-0"
                  options={[
                    { value: 'all', label: 'Todos' },
                    { value: 'unassigned', label: 'Sin asignar' },
                    ...advisors.map(a => ({ value: a.userId, label: a.name })),
                  ]}
                />
              </div>
            )}
          </div>

          {/* Búsqueda */}
          <div className="relative mb-3">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mid" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
            </svg>
            <input
              type="text"
              placeholder="Buscar sesiones..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-yellow text-sm placeholder-brand-mid"
            />
          </div>

          {/* Tabs Todo / No leídos — pill switcher */}
          <div className="flex items-center gap-1 bg-brand-light rounded-xl p-1">
            <button
              onClick={() => setFilterUnread(false)}
              className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition select-none cursor-pointer ${
                !filterUnread
                  ? 'bg-brand-surface text-brand-dark shadow-sm'
                  : 'text-brand-mid hover:text-brand-dark'
              }`}
            >
              Todo
            </button>
            <button
              onClick={() => setFilterUnread(true)}
              className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition flex items-center justify-center gap-1.5 select-none cursor-pointer ${
                filterUnread
                  ? 'bg-brand-surface text-brand-dark shadow-sm'
                  : 'text-brand-mid hover:text-brand-dark'
              }`}
            >
              No leídos
              {conversations.filter(c => (c.unreadCount ?? 0) > 0).length > 0 && (
                <span
                  className={`pointer-events-none text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 ${
                    filterUnread ? 'bg-brand-yellow text-brand-dark' : 'bg-brand-mid/20 text-brand-mid'
                  }`}
                >
                  {conversations.filter(c => (c.unreadCount ?? 0) > 0).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Lista de conversaciones */}
        <div
          ref={conversationsListRef}
          onScroll={handleConversationsScroll}
          className="flex-1 overflow-y-auto py-2"
        >
          {loading && filteredConversations.length === 0 ? (
            <div className="p-6 text-center text-brand-mid text-sm">Cargando...</div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-8 text-center text-brand-mid">
              <svg className="w-10 h-10 mx-auto mb-2 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-sm">No hay conversaciones</p>
            </div>
          ) : (
            filteredConversations.map((conversation) => {
              const isSelected = selectedConversation?.id === conversation.id;
              const displayName = conversation.participantName || conversation.phoneNumber;
              return (
                <button
                  key={conversation.id}
                  onClick={() => handleSelectConversation(conversation)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-all rounded-none ${conversation.assignedAdvisorName ? 'h-[80px]' : 'h-[64px]'}
                    ${isSelected
                      ? 'bg-brand-yellow/10 border-l-[3px] border-brand-yellow'
                      : 'hover:bg-brand-light border-l-[3px] border-transparent'
                    }`}
                >
                  {/* Avatar */}
                  <div className={`avatar-initial w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0
                    ${isSelected ? 'bg-brand-dark text-brand-yellow' : 'bg-brand-mid/20 text-brand-dark'}`}>
                    {displayName.charAt(0).toUpperCase()}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 overflow-hidden w-0">
                    <div className="flex justify-between items-baseline mb-0.5">
                      <p className={`text-sm truncate ${conversation.unreadCount && conversation.unreadCount > 0 ? 'font-bold text-brand-dark' : 'font-semibold text-brand-dark'}`}>
                        {displayName}
                      </p>
                      <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                        <span className="text-[10px] text-brand-mid whitespace-nowrap">{conversation.lastMessageTime}</span>
                        {(conversation.unreadCount ?? 0) > 0 && (
                          <span className="bg-brand-yellow text-brand-dark text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0">
                            {conversation.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className={`text-xs truncate block w-full ${conversation.unreadCount && conversation.unreadCount > 0 ? 'text-brand-dark font-medium' : 'text-brand-mid'}`}>
                      {conversation.lastMessage}
                    </p>
                    {conversation.assignedAdvisorName && (
                      <span className="inline-flex items-center gap-1 mt-2 px-1.5 py-0.5 rounded-md text-[10px] font-bold truncate max-w-full" style={{ backgroundColor: '#3B4155', color: '#FACF52' }}>
                        <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="truncate">{conversation.assignedAdvisorName}</span>
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
          {loading && filteredConversations.length > 0 && (
            <div className="p-4 text-center text-brand-mid text-xs">Cargando más...</div>
          )}
        </div>
      </div>

      {/* ── Handle de resize — fuera del panel para no ser cortado por overflow-hidden ── */}
      <div
        onMouseDown={onConvHandleMouseDown}
        title="Arrastra para redimensionar"
        className="hidden md:block relative w-1.5 flex-shrink-0 cursor-col-resize group z-10 bg-transparent"
      >
        <div className="absolute inset-y-0 left-0 w-px bg-transparent group-hover:bg-brand-yellow/60 transition-colors duration-150" />
        <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-1/2 w-3 h-8 rounded-full bg-brand-border group-hover:bg-brand-yellow/80 transition-colors duration-150 opacity-0 group-hover:opacity-100" />
      </div>

      {/* ── Panel derecho: área de mensajes ────────────────────────── */}
      <div className={`${showChatInMobile ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-brand-surface w-full md:w-auto`}>
        {selectedConversation ? (
          <>
            {/* Header del chat */}
            <div className="bg-brand-surface border-b border-brand-border px-3 md:px-6 py-3 md:py-4 flex items-center gap-2 md:gap-4 shadow-sm">
              {/* Volver (mobile) */}
              <button
                onClick={handleBackToConversations}
                className="md:hidden p-1.5 hover:bg-brand-light rounded-lg transition text-brand-mid flex-shrink-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Avatar */}
              <div className="avatar-initial w-9 h-9 md:w-10 md:h-10 rounded-full bg-brand-dark flex items-center justify-center text-brand-yellow font-bold text-sm flex-shrink-0">
                {(selectedConversation.participantName || selectedConversation.phoneNumber).charAt(0).toUpperCase()}
              </div>

              {/* Nombre y estado */}
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-brand-dark truncate text-sm">
                  {selectedConversation.participantName || 'Usuario'}
                </h3>
                {/* Badges — en mobile solo el teléfono + ventana, sin wrap */}
                <div className="flex items-center gap-1.5 flex-wrap md:flex-nowrap overflow-hidden">
                  <p className="text-xs text-brand-mid truncate max-w-[100px] md:max-w-none flex-shrink-0">{selectedConversation.phoneNumber}</p>
                  {selectedConversation.assignedAdvisorName && (
                    <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: '#3B4155', color: '#FACF52' }}>
                      <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {selectedConversation.assignedAdvisorName}
                    </span>
                  )}
                  {(() => {
                    const status = get24HourWindowStatus();
                    return (
                      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                        status.color === 'green'  ? 'bg-green-100 text-green-700' :
                        status.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {status.icon === 'open' && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
                        {status.icon === 'warn' && (
                          <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                          </svg>
                        )}
                        {status.icon === 'closed' && (
                          <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        )}
                        <span className="hidden sm:inline">{status.text}</span>
                      </span>
                    );
                  })()}
                  {selectedConversation.crmIntegrated && (
                    <span className="hidden md:inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-brand-yellow/20 text-brand-dark font-semibold flex-shrink-0">
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      CRM
                    </span>
                  )}
                </div>
              </div>

              {/* Acciones del header — en mobile solo íconos sin texto */}
              <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
                {/* Toggle agente — oculto para asesores */}
                {user?.role !== 'asesor' && (
                <div className="flex items-center gap-1.5 bg-brand-light rounded-xl px-2 md:px-3 py-1.5">
                  <span className="text-xs font-medium text-brand-dark hidden lg:inline">Agente</span>
                  {loadingAgent ? (
                    <div className="w-9 h-5 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 animate-spin text-brand-mid" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                    </div>
                  ) : (
                    <button
                      onClick={handleToggleAgent}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                        selectedConversation.agentEnabled !== false ? 'bg-brand-yellow' : 'bg-brand-border'
                      }`}
                      title={selectedConversation.agentEnabled !== false ? 'Desactivar agente' : 'Activar agente'}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full transition-transform shadow-sm ${
                        selectedConversation.agentEnabled !== false
                          ? 'translate-x-4 bg-white'
                          : 'translate-x-1 bg-brand-surface'
                      }`} />
                    </button>
                  )}
                </div>
                )}

                {/* Asignar asesor */}
                {(user?.role === 'administrador' || user?.role === 'operador') && (
                  <button
                    onClick={handleOpenAssignModal}
                    disabled={loadingAssign}
                    title="Asignar a asesor"
                    className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 bg-brand-light hover:bg-brand-mid/10 rounded-xl text-xs font-medium text-brand-dark transition disabled:opacity-50"
                  >
                    {loadingAssign ? (
                      <svg className="w-4 h-4 animate-spin text-brand-mid" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-brand-mid" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                    <span className="hidden sm:inline">{loadingAssign ? 'Asignando...' : 'Asignar'}</span>
                  </button>
                )}

                {/* Compartir webhook */}
                <button
                  onClick={handleOpenWebhookModal}
                  disabled={loading || !selectedCompany?.urlWebHook}
                  title="Enviar a webhook"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-light hover:bg-brand-mid/10 rounded-xl text-xs font-medium text-brand-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4 text-brand-mid" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  <span className="hidden sm:inline">Webhook</span>
                </button>
              </div>
            </div>

            {/* Área de mensajes */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3 bg-brand-bg">
              {loadingMessages ? (
                /* Skeleton de carga de mensajes */
                <div className="space-y-4 animate-pulse">
                  {/* Mensaje inbound skeleton */}
                  <div className="flex items-end gap-2 justify-start">
                    <div className="w-7 h-7 rounded-full bg-brand-border flex-shrink-0" />
                    <div className="space-y-1.5">
                      <div className="h-10 w-48 bg-brand-border rounded-2xl rounded-bl-sm" />
                      <div className="h-3 w-12 bg-brand-border rounded ml-auto" />
                    </div>
                  </div>
                  {/* Mensaje outbound skeleton */}
                  <div className="flex items-end gap-2 justify-end">
                    <div className="space-y-1.5">
                      <div className="h-14 w-64 bg-brand-border rounded-2xl rounded-br-sm" />
                      <div className="h-3 w-12 bg-brand-border rounded ml-auto" />
                    </div>
                    <div className="w-7 h-7 rounded-full bg-brand-border flex-shrink-0" />
                  </div>
                  {/* Mensaje inbound skeleton largo */}
                  <div className="flex items-end gap-2 justify-start">
                    <div className="w-7 h-7 rounded-full bg-brand-border flex-shrink-0" />
                    <div className="space-y-1.5">
                      <div className="h-20 w-56 bg-brand-border rounded-2xl rounded-bl-sm" />
                      <div className="h-3 w-12 bg-brand-border rounded ml-auto" />
                    </div>
                  </div>
                  {/* Mensaje outbound skeleton corto */}
                  <div className="flex items-end gap-2 justify-end">
                    <div className="space-y-1.5">
                      <div className="h-10 w-32 bg-brand-border rounded-2xl rounded-br-sm" />
                      <div className="h-3 w-12 bg-brand-border rounded ml-auto" />
                    </div>
                    <div className="w-7 h-7 rounded-full bg-brand-border flex-shrink-0" />
                  </div>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-brand-mid">
                    <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-sm">Inicia una conversación</p>
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <div key={message.id} className={`flex items-end gap-2 ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                      {/* Avatar cliente (inbound) — inicial del nombre/teléfono */}
                      {message.direction === 'inbound' && (
                        <div className="w-7 h-7 rounded-full bg-brand-surface border border-brand-border flex items-center justify-center flex-shrink-0 mb-1 text-[10px] font-bold text-brand-dark dark:bg-brand-light dark:text-white">
                          {(selectedConversation.participantName || selectedConversation.phoneNumber).charAt(0).toUpperCase()}
                        </div>
                      )}

                      <div className={`max-w-[75%] md:max-w-lg px-4 py-2.5 rounded-2xl shadow-sm border border-gray-100 dark:border-transparent ${
                        message.direction === 'outbound'
                          ? 'bg-white text-gray-900 rounded-br-sm dark:bg-[#2E3252] dark:text-white'
                          : 'bg-white text-gray-900 rounded-bl-sm dark:bg-[#2E3252] dark:text-white'
                      }`}>
                        {message.s3Key && message.mediaType && (
                          <MediaViewer
                            s3Key={message.s3Key}
                            mediaType={message.mediaType}
                            mediaFileName={message.mediaFileName}
                            direction={message.direction}
                          />
                        )}
                        <p className="text-sm break-words whitespace-pre-wrap leading-relaxed">{message.content}</p>
                        <p className="text-[10px] mt-1 text-right text-gray-400 dark:text-white/50">
                          {new Date(message.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                          {message.direction === 'outbound' && (
                            <span className="ml-1 inline-flex items-center">
                              {message.status === 'sending' && (
                                <svg className="w-3 h-3 animate-spin opacity-60" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                              )}
                              {message.status === 'sent' && (
                                <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                              {message.status === 'error' && (
                                <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z" />
                                </svg>
                              )}
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Avatar bot (outbound) — ícono de robot */}
                      {message.direction === 'outbound' && (
                        <div className="avatar-initial w-7 h-7 rounded-full bg-brand-dark flex items-center justify-center flex-shrink-0 mb-1">
                          <svg className="w-4 h-4 text-brand-yellow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            {/* Antena */}
                            <line x1="12" y1="2" x2="12" y2="5" />
                            <circle cx="12" cy="2" r="1" fill="currentColor" stroke="none" />
                            {/* Cabeza */}
                            <rect x="4" y="5" width="16" height="12" rx="2" />
                            {/* Ojos */}
                            <circle cx="9" cy="11" r="1.5" fill="currentColor" stroke="none" />
                            <circle cx="15" cy="11" r="1.5" fill="currentColor" stroke="none" />
                            {/* Boca */}
                            <path d="M9 14.5h6" strokeWidth="1.5" />
                            {/* Orejas/conectores */}
                            <line x1="4" y1="10" x2="2" y2="10" />
                            <line x1="20" y1="10" x2="22" y2="10" />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input de mensaje */}
            <div className="bg-brand-surface border-t border-brand-border px-4 py-3">
              {/* Barra de progreso de subida */}
              {isUploading && (
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-brand-mid mb-1">
                    <span>Subiendo archivo...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-brand-light rounded-full h-1.5">
                    <div className="bg-brand-dark h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                  </div>
                </div>
              )}

              <div className="flex items-end gap-2">
                {/* Plantilla — en mobile va al final */}
                <button
                  onClick={() => setShowTemplateModal(true)}
                  disabled={loading || !selectedCompany?.accessToken}
                  title="Enviar plantilla"
                  className="order-3 md:order-none p-2.5 bg-brand-light hover:bg-brand-mid/10 rounded-xl transition text-brand-mid disabled:opacity-40 flex-shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>

                {/* Adjuntar archivo — en mobile va al final */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  title="Enviar archivo"
                  className="order-4 md:order-none p-2.5 bg-brand-light hover:bg-brand-mid/10 rounded-xl transition text-brand-mid disabled:opacity-40 flex-shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                />

                {/* Campo de texto — textarea para saltos de línea */}
                <div className="order-1 md:order-none flex-1 bg-brand-surface border border-brand-border rounded-xl px-4 py-2.5 flex items-end focus-within:ring-2 focus-within:ring-brand-yellow transition-shadow">
                  <textarea
                    ref={textareaRef}
                    rows={1}
                    placeholder="Escribe un comando o instrucción..."
                    value={replyMessage}
                    onChange={(e) => {
                      setReplyMessage(e.target.value);
                      // Auto-crecer hasta 5 líneas
                      e.target.style.height = 'auto';
                      e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                        // Enter solo → enviar
                        e.preventDefault();
                        handleSendReply();
                      } else if (e.key === 'Enter' && e.ctrlKey) {
                        // Ctrl+Enter → insertar salto de línea manualmente
                        e.preventDefault();
                        const el = e.target as HTMLTextAreaElement;
                        const start = el.selectionStart;
                        const end = el.selectionEnd;
                        const newValue = replyMessage.substring(0, start) + '\n' + replyMessage.substring(end);
                        setReplyMessage(newValue);
                        // Reposicionar el cursor después del salto
                        requestAnimationFrame(() => {
                          el.selectionStart = start + 1;
                          el.selectionEnd = start + 1;
                          el.style.height = 'auto';
                          el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
                        });
                      }
                      // Shift+Enter → salto de línea nativo del textarea
                    }}
                    className="flex-1 bg-transparent focus:outline-none text-sm text-brand-dark placeholder-brand-mid resize-none overflow-hidden leading-5 border-0"
                    style={{ height: '20px' }}
                  />
                </div>

                {/* Enviar */}
                <button
                  onClick={handleSendReply}
                  disabled={loading || isUploading || !replyMessage.trim()}
                  title="Enviar (Enter)"
                  className="order-2 md:order-none p-2.5 rounded-xl transition flex-shrink-0 disabled:cursor-not-allowed
                    bg-[#FACF52] text-[#3B4155] hover:opacity-90
                    disabled:bg-brand-light disabled:text-brand-mid disabled:opacity-60"
                >
                  {loading ? (
                    <Spinner size="sm" color="white" />
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Estado vacío */
          <div className="flex items-center justify-center h-full bg-brand-bg">
            <div className="text-center text-brand-mid">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-brand-surface shadow-sm flex items-center justify-center">
                <svg className="w-10 h-10 text-brand-mid/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-brand-dark">Selecciona una conversación</p>
              <p className="text-xs mt-1">Elige un chat de la lista para comenzar</p>
            </div>
          </div>
        )}
      </div>

      {/* Webhook Modal */}
      {showWebhookModal && webhookData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-brand-dark text-white p-6">
              <h2 className="text-lg font-bold">Validar datos para enviar</h2>
              <p className="text-brand-mid text-sm mt-0.5">Revisa los datos antes de enviar al webhook</p>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-100 bg-brand-light">
              <button
                onClick={() => setWebhookModalTab('form')}
                className={`flex-1 px-4 py-3 font-semibold text-sm transition ${
                  webhookModalTab === 'form'
                    ? 'text-brand-dark border-b-2 border-brand-yellow bg-white'
                    : 'text-brand-mid hover:text-brand-dark'
                }`}
              >
                Formulario
              </button>
              <button
                onClick={() => setWebhookModalTab('questions')}
                className={`flex-1 px-4 py-3 font-semibold text-sm transition ${
                  webhookModalTab === 'questions'
                    ? 'text-brand-dark border-b-2 border-brand-yellow bg-white'
                    : 'text-brand-mid hover:text-brand-dark'
                }`}
              >
                Preguntas
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {webhookModalTab === 'form' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-brand-dark mb-1">Nombre</label>
                    <input type="text" value={webhookData.participantName}
                      onChange={(e) => setWebhookData({ ...webhookData, participantName: e.target.value })}
                      className="w-full px-3 py-2 bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-yellow text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-brand-dark mb-1">Teléfono</label>
                    <input type="text" value={webhookData.phoneNumber}
                      onChange={(e) => setWebhookData({ ...webhookData, phoneNumber: e.target.value })}
                      className="w-full px-3 py-2 bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-yellow text-sm font-mono" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-brand-dark mb-1">Fuente de Contacto</label>
                    <select value={webhookData.contactSource}
                      onChange={(e) => setWebhookData({ ...webhookData, contactSource: e.target.value })}
                      className="w-full px-3 py-2 bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-yellow text-sm">
                      <option value="4">WhatsApp</option>
                      <option value="3">Facebook</option>
                      <option value="10">TikTok</option>
                      <option value="2">Formulario Web</option>
                      <option value="5">Llamada Mensaje Directo</option>
                      <option value="8">Referido</option>
                      <option value="6">Cliente Antiguo</option>
                      <option value="65">Feria EXPOMIN</option>
                      <option value="11">Formulario Adex</option>
                      <option value="64">Referido CALIDDA</option>
                      <option value="7">Referido por Alicorp</option>
                      <option value="9">Visita Presencial</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-brand-dark mb-1">Sector</label>
                    <select value={webhookData.sector}
                      onChange={(e) => setWebhookData({ ...webhookData, sector: e.target.value })}
                      className="w-full px-3 py-2 bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-yellow text-sm">
                      {sectorOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.valor}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-brand-dark mb-1">Condición</label>
                    <select value={webhookData.condicion}
                      onChange={(e) => setWebhookData({ ...webhookData, condicion: e.target.value })}
                      className="w-full px-3 py-2 bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-yellow text-sm">
                      {condicionOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.valor}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-brand-dark mb-1">Descripción</label>
                    <textarea value={webhookData.descripcion}
                      onChange={(e) => setWebhookData({ ...webhookData, descripcion: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-yellow text-sm"
                      placeholder="Resumen de respuestas del cliente" />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {webhookData.questionsAnswers.length === 0 ? (
                    <p className="text-sm text-gray-500">No hay preguntas respondidas</p>
                  ) : (
                    webhookData.questionsAnswers.map((qa: any, idx: number) => (
                      <div key={idx} className="bg-gray-50 p-3 rounded border border-gray-200">
                        <p className="text-xs font-semibold text-gray-700 mb-1">P: {qa.question}</p>
                        <p className="text-xs text-gray-600">R: {qa.answer}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="border-t border-brand-border p-5 flex gap-3 justify-end bg-brand-surface">
              <Button
                variant="secondary"
                onClick={() => { setShowWebhookModal(false); setWebhookData(null); }}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleSendToWebhook}
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Enviando...' : 'Enviar al Webhook'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal asignar conversación */}
      {showAssignModal && selectedConversation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-surface rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl relative">
            {/* Overlay de carga dentro del modal */}
            {loadingAssign && (
              <div className="absolute inset-0 bg-brand-surface/80 rounded-2xl flex flex-col items-center justify-center z-10 gap-3">
                <svg className="w-8 h-8 animate-spin text-brand-yellow" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <p className="text-sm font-semibold text-brand-dark">Procesando asignación...</p>
              </div>
            )}
            <h2 className="text-lg font-bold text-brand-dark mb-1">Asignar Conversación</h2>
            <p className="text-xs text-brand-mid mb-4">{selectedConversation.participantName || selectedConversation.phoneNumber}</p>

            {currentAssignment ? (
              <div className="mb-4 p-4 bg-brand-yellow/10 border border-brand-yellow/30 rounded-xl">
                <p className="text-xs font-bold text-brand-dark mb-1">Asignado actualmente a:</p>
                <p className="font-semibold text-brand-dark text-sm">{currentAssignment.advisorName}</p>
                {currentAssignment.advisorEmail && (
                  <p className="text-xs text-brand-mid">{currentAssignment.advisorEmail}</p>
                )}
                {currentAssignment.assignedByName && (
                  <p className="text-xs text-brand-mid mt-1">Por: {currentAssignment.assignedByName}{currentAssignment.assignedAt ? ` · ${new Date(currentAssignment.assignedAt).toLocaleString('es-ES')}` : ''}</p>
                )}
                <Button
                  variant="danger"
                  fullWidth
                  onClick={handleUnassignConversation}
                  disabled={loadingAssign}
                  loading={loadingAssign}
                  className="mt-3"
                >
                  Desasignar
                </Button>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-brand-light rounded-xl">
                <p className="text-xs text-brand-mid">Esta conversación no está asignada a ningún asesor</p>
              </div>
            )}

            <p className="text-xs font-bold text-brand-dark mb-2">{currentAssignment ? 'Reasignar a:' : 'Asignar a:'}</p>
            <div className="space-y-2 max-h-52 overflow-y-auto mb-4">
              {advisors.length === 0 ? (
                <p className="text-center text-brand-mid text-sm py-4">No hay asesores disponibles</p>
              ) : (
                advisors.map((advisor) => {
                  const isCurrentlyAssigned = currentAssignment?.userId === advisor.userId;
                  return (
                    <button key={advisor.userId}
                      onClick={() => !isCurrentlyAssigned && handleAssignConversation(advisor.userId)}
                      disabled={loadingAssign || isCurrentlyAssigned}
                      className={`w-full text-left px-4 py-3 rounded-xl border transition ${
                        isCurrentlyAssigned
                          ? 'bg-brand-yellow/10 border-brand-yellow/40 cursor-not-allowed'
                          : 'border-gray-100 hover:bg-brand-light hover:border-brand-yellow/30'
                      } disabled:opacity-60`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-brand-dark text-sm">{advisor.name}</p>
                          <p className="text-xs text-brand-mid">{advisor.email}</p>
                        </div>
                        {isCurrentlyAssigned && <span className="text-xs text-brand-dark font-bold bg-brand-yellow px-2 py-0.5 rounded-full">Actual</span>}
                        {loadingAssign && !isCurrentlyAssigned && (
                          <svg className="w-3.5 h-3.5 animate-spin text-brand-mid opacity-0" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            <Button
              variant="secondary"
              fullWidth
              onClick={() => { setShowAssignModal(false); setCurrentAssignment(null); }}
              disabled={loadingAssign}
            >
              Cerrar
            </Button>
          </div>
        </div>
      )}
      
      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />
      
      {/* WhatsApp Template Modal */}
      {showTemplateModal && selectedConversation && selectedCompany && (
        <WhatsAppTemplateModal
          isOpen={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          onSend={handleSendTemplate}
          phoneNumberId={selectedCompany.phoneNumberId}
        />
      )}
    </div>
  );
};

export default ConversationsPage;
